/**
 * Start-up: one tab only → load SQLite (WASM) and the database file from IndexedDB → FieldApp
 * (the reused mobile core + polling outbox) → wire sync triggers (online, foreground) → render.
 */
import initSqlJs from "sql.js";
import wasmUrl from "sql.js/dist/sql-wasm.wasm?url";
import React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { FieldApp } from "./field/app";
import { STRINGS } from "./i18n";
import { acquireSingleTabLock, localKV, makeId, prefs, registerServiceWorker, requestPersistentStorage, watchOnline } from "./platform/browser";
import { apiBaseUrl } from "./platform/config";
import { IdbByteStore } from "./platform/idb";
import { MemoryByteStore, SqlJsDb, type ByteStore } from "./platform/sqljsDb";
import { AppProvider } from "./ui/context";
import "./styles.css";

const root = createRoot(document.getElementById("root")!);
const t = STRINGS[prefs.get("lang") === "en" ? "en" : "hi"];

function fatal(message: string) {
  root.render(
    <div className="center">
      <p className="banner banner-error" role="alert">
        {message}
      </p>
    </div>
  );
}

/** Start-up stage, visible as <html data-boot="…"> (support / e2e diagnostics). */
const stage = (s: string) => {
  document.documentElement.dataset.boot = s;
};

async function start() {
  const sw = registerServiceWorker();

  stage("lock");
  if (!(await acquireSingleTabLock())) return fatal(t.oneTab);

  stage("storage");
  let bytes: ByteStore = new IdbByteStore();
  let storageProblem: string | null = null;
  try {
    await bytes.load("probe");
  } catch {
    bytes = new MemoryByteStore();
    storageProblem = t.privateMode;
  }

  stage("sqlite");
  const SQL = await initSqlJs({ locateFile: () => wasmUrl });
  stage("db");
  const db = await SqlJsDb.open({ SQL, store: bytes });
  stage("app");
  const app = await FieldApp.create({ db, kv: localKV, makeId, baseUrl: apiBaseUrl });
  // a driver link just opened / pasted: the duty screen starts the duty by itself
  app.autoStartDrive = () => prefs.set("drive.autostart", "1");

  watchOnline((online) => {
    app.setOnline(online);
    if (online) {
      sw.checkForUpdate();
      void app.refreshMySos();
    }
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void app.syncNow("foreground");
      void app.refreshMySos();
    }
  });
  if (app.token) {
    void app.syncNow("startup");
    void requestPersistentStorage();
  }
  app.core.changes.subscribe(() => {
    if (app.token) void requestPersistentStorage();
  });

  // for the end-to-end test and support: what is waiting on this phone
  (window as unknown as { __npacField?: unknown }).__npacField = {
    status: () => app.status(),
    outbox: async () => ({ core: await app.core.store.listOutbox(), polling: await app.polling.list() }),
  };

  stage("ready");
  root.render(
    <React.StrictMode>
      <AppProvider app={app}>
        <App sw={sw} storageProblem={storageProblem} />
      </AppProvider>
    </React.StrictMode>
  );
}

start().catch((e) => fatal(`${t.err_generic}: ${e instanceof Error ? e.message : String(e)}`));
